import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent } from "@/components/ui/card";
import { TeacherTrialStats } from "@/hooks/useTeacherTrialStats";
import { useTeacherTrialDetails } from "@/hooks/useTeacherTrialDetails";
import { format } from "date-fns";
import { th } from "date-fns/locale";

interface TeacherTrialDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  teacher: TeacherTrialStats | null;
  startDate?: string;
  endDate?: string;
}

export const TeacherTrialDetailModal: React.FC<TeacherTrialDetailModalProps> = ({
  isOpen,
  onClose,
  teacher,
  startDate,
  endDate,
}) => {
  const { data: details, isLoading } = useTeacherTrialDetails(
    teacher?.teacher_code || null,
    startDate,
    endDate
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "ปิดการขายเรียบร้อย":
        return <Badge className="bg-green-500 hover:bg-green-600">✅ {status}</Badge>;
      case "ปิดการขายไม่สำเร็จ":
        return <Badge variant="destructive">❌ {status}</Badge>;
      case "ครูส่งผลประเมินแล้ว":
        return <Badge className="bg-yellow-500 hover:bg-yellow-600">✉️ {status}</Badge>;
      case "รอครูส่งผลประเมิน":
        return <Badge variant="secondary">⏳ {status}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, "dd/MM/yy", { locale: th });
    } catch {
      return dateString;
    }
  };

  const formatCost = (cost: number | null) => {
    if (cost === null || cost === undefined) return "-";
    return new Intl.NumberFormat("th-TH").format(cost);
  };

  if (!teacher) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl">
            📋 รายละเอียดทดลองเรียน - ครู {teacher.teacher_nickname || teacher.teacher_name} ({teacher.teacher_code})
          </DialogTitle>
        </DialogHeader>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4 py-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-muted-foreground">ทดลองเรียนทั้งหมด</div>
              <div className="text-2xl font-bold">{teacher.total_trials} ครั้ง</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-muted-foreground">ปิดการขายสำเร็จ</div>
              <div className="text-2xl font-bold text-green-600">{teacher.successful_trials} ครั้ง</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-muted-foreground">อัตราความสำเร็จ</div>
              <div className="text-2xl font-bold text-primary">{teacher.success_rate.toFixed(1)}%</div>
            </CardContent>
          </Card>
        </div>

        {/* Detail Table */}
        <ScrollArea className="flex-1 border rounded-md">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : !details || details.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              ไม่พบข้อมูลทดลองเรียน
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[90px]">วันที่</TableHead>
                  <TableHead>นักเรียน</TableHead>
                  <TableHead className="w-[70px]">ระดับชั้น</TableHead>
                  <TableHead className="w-[90px]">ประเภท</TableHead>
                  <TableHead className="w-[70px]">สถานที่</TableHead>
                  <TableHead className="w-[80px]">Tier</TableHead>
                  <TableHead className="w-[80px] text-right">ราคา</TableHead>
                  <TableHead className="w-[180px]">สถานะ</TableHead>
                  <TableHead className="w-[100px]">Order</TableHead>
                  <TableHead>หมายเหตุ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {details.map((detail, index) => (
                  <TableRow key={detail.id || index}>
                    <TableCell className="font-medium">
                      {formatDate(detail.class_date)}
                    </TableCell>
                    <TableCell>
                      <div>
                        <span className="font-medium">{detail.student_nickname || "-"}</span>
                        {detail.student_name && (
                          <span className="text-muted-foreground text-sm ml-1">
                            ({detail.student_name})
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{detail.grade || "-"}</TableCell>
                    <TableCell>{detail.class_type || "-"}</TableCell>
                    <TableCell>{detail.location || "-"}</TableCell>
                    <TableCell>{detail.tier || "-"}</TableCell>
                    <TableCell className="text-right">{formatCost(detail.cost)}</TableCell>
                    <TableCell>{getStatusBadge(detail.status)}</TableCell>
                    <TableCell>
                      {detail.order_code ? (
                        <Badge variant="outline">{detail.order_code}</Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate" title={detail.note || ""}>
                      {detail.note || "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </ScrollArea>

        {/* Legend */}
        <div className="pt-4 border-t text-sm text-muted-foreground">
          <span className="mr-4">✅ ปิดการขายเรียบร้อย</span>
          <span className="mr-4">❌ ปิดการขายไม่สำเร็จ</span>
          <span className="mr-4">✉️ ครูส่งผลประเมินแล้ว</span>
          <span>⏳ รอครูส่งผลประเมิน</span>
        </div>
      </DialogContent>
    </Dialog>
  );
};
